import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js'
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions'
import { getSearchClient, getSearchConfig } from './client.js'
import { craftSearchUrl, createGuardedSearchClient } from './guard.js'
import {
    buildSearchResultTracking,
    trackNavigationSearch,
    trackResultClick,
    trackSearchResultInteraction,
    trackSearchResultInteractionNow,
} from './insights.js'

const config = getSearchConfig()
const baseClient = getSearchClient()
const desktopMount = document.querySelector('[data-search-autocomplete-desktop]')
const mobileMount = document.querySelector('[data-search-autocomplete-mobile]')

if (config.provider === 'algolia' && baseClient && desktopMount && mobileMount) {
    for (const mount of [desktopMount, mobileMount]) {
        mount.addEventListener('pointerdown', event => {
            event.target.closest('.aa-Form')?.classList.add('aa-Form--pointer-focus')
        })
        mount.addEventListener('keydown', event => {
            if (['Tab', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
                event.target.closest('.aa-Form')?.classList.remove('aa-Form--pointer-focus')
            }
        })
        mount.addEventListener('focusout', event => {
            const form = event.target.closest('.aa-Form')
            if (form && !form.contains(event.relatedTarget)) {
                form.classList.remove('aa-Form--pointer-focus')
            }
        })
    }

    const desktopMedia = window.matchMedia('(min-width: 1100px)')
    const attributesToRetrieve = [
        'title', 'url', 'kind', 'collectionType', 'resourceType',
        'image', 'sports', 'productTypes', 'fileExtension', 'access',
    ]
    const groups = [
        { id: 'collections', label: 'Collections', filter: 'kind:page AND isCollection:true', count: 1 },
        { id: 'products', label: 'Products', filter: 'kind:product', count: 5 },
        { id: 'resources', label: 'Resources', filter: 'kind:resource', count: 2 },
        { id: 'pages', label: 'Pages', filter: 'kind:page AND isCollection:false', count: 2 },
    ]
    const searchUrl = (query, type) => `/search?q=${encodeURIComponent(query.trim())}${type ? `&type=${type}` : ''}`
    const trustedNavigationPath = (value) => {
        if (typeof value !== 'string' || !value.trim()) return null

        try {
            const url = new URL(value, window.location.origin)
            if (
                !['http:', 'https:'].includes(url.protocol) ||
                url.origin !== window.location.origin ||
                url.username ||
                url.password
            ) return null

            return `${url.pathname}${url.search}${url.hash}`
        } catch {
            return null
        }
    }
    let activeSurface
    let instance
    let latestQuery = ''
    let failedQuery = ''
    let activeGeneration = 0
    let pendingKeyboardNavigation = null

    const resultsHref = (query, type) => {
        const q = String(query ?? '').trim()
        if (q && q === failedQuery) return craftSearchUrl(q)
        return searchUrl(query, type)
    }

    const searchClient = createGuardedSearchClient(baseClient, {
        onFailure: ({ query }) => {
            const q = (query || latestQuery || '').trim()
            if (!q) return
            failedQuery = q
            if (instance) instance.setIsOpen(false)
        },
    })

    // Keep requester descriptions intact so Autocomplete can batch all five
    // sources through the same lite client. A failed batch simply closes the panel.
    const safeResults = (description, query) => {
        const execute = description.execute
        const generation = activeGeneration
        return {
            ...description,
            execute: params => Promise.resolve().then(() => execute(params)).catch((error) => {
                if (!error?.isGuardStale && generation === activeGeneration && query === latestQuery) {
                    failedQuery = query
                    if (instance) instance.setIsOpen(false)
                }
                return params.requests.map(request => ({
                    items: { hits: [] },
                    sourceId: request.sourceId,
                    transformResponse: request.transformResponse,
                }))
            }),
        }
    }

    const createContentSource = (group, query) => ({
        sourceId: group.id,
        getItems() {
            if (query !== latestQuery) return []
            return safeResults(getAlgoliaResults({
                searchClient,
                transformResponse({ results }) {
                    const result = results[0]
                    const page = Number(result?.page)
                    const hitsPerPage = Number(result?.hitsPerPage)
                    const hits = (result?.hits || []).map((hit, index) => {
                        const url = trustedNavigationPath(hit.url)
                        if (!url) return null

                        return {
                            ...hit,
                            url,
                            __totalHits: result.nbHits,
                            __position: Number.isInteger(hit.__position) && hit.__position > 0
                                ? hit.__position
                                : Number.isInteger(page) && page >= 0 && Number.isInteger(hitsPerPage) && hitsPerPage > 0
                                    ? (page * hitsPerPage) + index + 1
                                    : undefined,
                        }
                    }).filter(Boolean)
                    if (hits.length && (group.id === 'products' || group.id === 'resources')) {
                        hits.unshift({
                            objectID: `view-all-${group.id}`,
                            __viewAll: true,
                            __totalHits: result.nbHits,
                            url: resultsHref(query, group.id),
                        })
                    }
                    return hits
                },
                queries: [{
                    indexName: config.indexName,
                    query,
                    params: {
                        filters: group.filter,
                        hitsPerPage: group.count,
                        attributesToRetrieve,
                        attributesToHighlight: ['title'],
                        clickAnalytics: true,
                        analyticsTags: ['flyout'],
                    },
                }],
            }), query)
        },
        getItemUrl({ item }) {
            return item.url
        },
        onSelect({ event, item, itemUrl, setIsOpen }) {
            if (!item.__viewAll) {
                trackResultClick({
                    indexName: item.__autocomplete_indexName || config.indexName,
                    queryID: item.__queryID || item.__autocomplete_queryID,
                    objectID: item.objectID,
                    position: item.__position,
                })
                if (event?.type === 'keydown') {
                    if (event.metaKey || event.ctrlKey || event.shiftKey) {
                        // The original tab remains open, so GTM does not need
                        // to delay navigation to the new tab or window.
                        trackSearchResultInteractionNow(item.kind)
                    } else if (!event.altKey) {
                        pendingKeyboardNavigation = {
                            itemUrl,
                            kind: item.kind,
                        }
                    }
                }
            }
            setIsOpen(false)
        },
        templates: {
            header({ html }) {
                return html`<div class="header-autocomplete__section-heading">
                    <span class="header-autocomplete__heading">${group.label}</span>
                </div>`
            },
            item({ item, components, html }) {
                if (item.__viewAll) {
                    return html`<a class="header-autocomplete__section-link" href=${item.url}>
                        View all ${item.__totalHits} ${group.label}
                        <span class="header-autocomplete__arrow" aria-hidden="true">
                            <svg width="4" height="8" viewBox="0 0 4 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0.5 7.5L4 4L0.5 0.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </span>
                    </a>`
                }
                const protectedPage = item.access === 'passwordProtected'
                const image = !protectedPage && item.image && item.image.url ? item.image : null
                return html`<a class=${`header-autocomplete__result${image ? ' header-autocomplete__result--with-image' : ''}`} href=${item.url} data-tracking=${buildSearchResultTracking(item.kind)}>
                    ${image ? html`<img class="header-autocomplete__image" src=${image.url} alt="" loading="lazy" />` : ''}
                    <span class="header-autocomplete__copy">
                        <span class="header-autocomplete__title">${protectedPage
                            ? item.title
                            : html`<${components.Highlight} hit=${item} attribute="title" />`}</span>
                    </span>
                </a>`
            },
        },
    })

    const createSuggestionPlugin = () => {
        if (!config.querySuggestionsIndexName) return null

        return createQuerySuggestionsPlugin({
            searchClient,
            indexName: config.querySuggestionsIndexName,
            getSearchParams() {
                return {
                    hitsPerPage: 4,
                    attributesToRetrieve: ['query'],
                    attributesToHighlight: ['query'],
                    clickAnalytics: true,
                    analyticsTags: ['flyout'],
                }
            },
            transformSource({ source }) {
                const getItems = source.getItems
                return {
                    ...source,
                    getItems(params) {
                        if (params.query.trim().length < 2 || params.query !== latestQuery) return []
                        return safeResults(getItems(params), params.query)
                    },
                    onSelect({ setIsOpen, setActiveItemId }) {
                        // Autocomplete already replaces the query and fetches
                        // its results before calling this handler.
                        setActiveItemId(null)
                        setIsOpen(true)
                    },
                    templates: {
                        header({ html }) {
                            return html`<div class="header-autocomplete__heading">Suggestions</div>`
                        },
                        item({ item, html }) {
                            return html`<span class="header-autocomplete__suggestion">${item.query}</span>`
                        },
                    },
                }
            },
        })
    }

    const createInstance = surface => {
        const isDesktop = surface === 'desktop'
        const container = isDesktop ? desktopMount : mobileMount
        const suggestionPlugin = createSuggestionPlugin()
        activeGeneration += 1
        latestQuery = ''
        failedQuery = ''

        instance = autocomplete({
            container,
            plugins: suggestionPlugin ? [suggestionPlugin] : [],
            navigator: {
                navigate({ itemUrl }) {
                    const pending = pendingKeyboardNavigation
                    pendingKeyboardNavigation = null
                    const path = trustedNavigationPath(itemUrl)
                    if (!path) return
                    const navigate = () => window.location.assign(path)

                    if (!pending || trustedNavigationPath(pending.itemUrl) !== path || !trackSearchResultInteraction(pending.kind, navigate)) {
                        navigate()
                    }
                },
            },
            placeholder: 'What can we help you find?',
            // The mobile design keeps the input in the navigation and shows
            // the results panel directly below it.
            detachedMediaQuery: '(max-width: 0px)',
            onStateChange({ state }) {
                if (!isDesktop) {
                    document.body.classList.toggle('header-autocomplete-mobile-open', state.isOpen)
                }
            },
            openOnFocus: true,
            defaultActiveItemId: null,
            stallThreshold: 300,
            shouldPanelOpen({ state }) {
                if (state.query.trim().length < 2 || state.query === failedQuery) return false
                return state.collections.some(({ items }) => items.length > 0)
            },
            getSources({ query }) {
                if (query !== latestQuery) failedQuery = ''
                latestQuery = query
                if (query.trim().length < 2) return []

                // Delaying source resolution also delays the plugin's source:
                // Autocomplete waits for both before issuing one multi-query.
                return new Promise(resolve => {
                    window.setTimeout(() => {
                        if (query !== latestQuery) {
                            resolve([])
                            return
                        }

                        resolve([
                            ...groups.map(group => createContentSource(group, query)),
                            {
                                sourceId: 'seeAll',
                                getItems() {
                                    return [{ query }]
                                },
                                getItemUrl({ item }) {
                                    return resultsHref(item.query)
                                },
                                templates: {
                                    header({ html, state }) {
                                        const hasResults = state.collections.some(({ source, items }) =>
                                            source.sourceId !== 'seeAll' && items.length > 0
                                        )
                                        return state.status === 'idle' && !hasResults
                                            ? html`<p class="header-autocomplete__empty" role="status">
                                                No results found for “${state.query}”. Try a different search term.
                                            </p>`
                                            : null
                                    },
                                    item({ item, html }) {
                                        const label = `See all results for '${item.query}'`
                                        return html`<a class="header-autocomplete__see-all" href=${resultsHref(item.query)} aria-label=${label}>
                                            ${label}
                                        </a>`
                                    },
                                },
                            },
                        ])
                    }, 200)
                })
            },
            onSubmit({ state }) {
                if (!state.query.trim()) return

                // Craft mode tracks its Twig form button. Algolia mode renders
                // only this Autocomplete mount, so it must track submission here.
                const navigate = () => window.location.assign(resultsHref(state.query))
                if (!trackNavigationSearch(navigate)) navigate()
            },
        })
    }

    const swapSurface = () => {
        const nextSurface = desktopMedia.matches ? 'desktop' : 'mobile'
        if (nextSurface === activeSurface) return

        if (instance) {
            const oldContainer = activeSurface === 'desktop' ? desktopMount : mobileMount
            const focused = document.activeElement
            const wasInsideSearch = focused && (
                desktopMount.contains(focused) ||
                mobileMount.contains(focused) ||
                Boolean(focused.closest('.aa-Panel'))
            )
            if (wasInsideSearch) focused.blur()
            instance.setIsOpen(false)
            instance.destroy()
            instance = null
            document.body.classList.remove('header-autocomplete-mobile-open')
            oldContainer.replaceChildren()
            if (wasInsideSearch) {
                const stableControl = document.querySelector('.hamburger, .logo a')
                if (stableControl) stableControl.focus({ preventScroll: true })
            }
        }

        activeSurface = nextSurface
        createInstance(nextSurface)
    }

    swapSurface()
    if (desktopMedia.addEventListener) {
        desktopMedia.addEventListener('change', swapSurface)
    } else {
        desktopMedia.addListener(swapSurface)
    }
}
